import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type RunLogLinesQuery,
  type RunLogLinesQueryVariables,
  useRunLogLinesQuery,
} from '../../generated/graphql';
import type {
  ApolloError,
  QueryResult,
} from '../../state/graphql/apolloWithAbort';
import {propagateErrorsContext} from '../../util/errors';
import {
  type LogLines,
  PAGE_SIZE_IMPROVED_PAGINATION_MAX,
  PAGE_SIZE_MAX,
  usePolling,
  useRetryInitialLoad,
  type UseRunLogQueryParams,
} from './runLogQueryCommon';

export {
  type LogLines,
  PAGE_SIZE_IMPROVED_PAGINATION_MAX,
  PAGE_SIZE_MAX,
  RUN_LOG_LINES_QUERY,
  type UseRunLogQueryParams,
} from './runLogQueryCommon';

export interface UseRunLogQueryResult {
  error?: ApolloError;
  loading: boolean;
  logLines: LogLines;
  retryInitialLoad?: () => void;
  // Backward pagination (older logs)
  fetchMorePrevious?: () => void;
  fetchMorePreviousError?: ApolloError;
  hasPrevious?: boolean;
  isLoadingPrevious?: boolean;
  isRetryingPrevious?: boolean;
  // Forward pagination (newer logs)
  fetchMoreNext?: () => void;
  fetchMoreNextError?: ApolloError;
  hasNext?: boolean;
  isLoadingNext?: boolean;
  isRetryingNext?: boolean;
  // Polling control
  canPoll?: boolean;
  isPolling?: boolean;
  togglePolling?: (isPolling: boolean) => void;
}

export function useRunLogQuery({
  entityName,
  pollInterval,
  projectName,
  runName,
  skip = false,
  useImprovedPagination = false,
}: UseRunLogQueryParams): UseRunLogQueryResult {
  const pageSize = useImprovedPagination
    ? PAGE_SIZE_IMPROVED_PAGINATION_MAX
    : PAGE_SIZE_MAX;

  const {data, loading, error, fetchMore, refetch} = useRunLogLinesQuery({
    context: propagateErrorsContext(),
    pollInterval: useImprovedPagination ? undefined : pollInterval,
    skip,
    variables: {
      entityName,
      projectName,
      runName,
      last: pageSize,
      useImprovedPagination,
    },
  });

  const [isRetrying, setIsRetrying] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [isRetryingPrevious, setIsRetryingPrevious] = useState(false);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isRetryingNext, setIsRetryingNext] = useState(false);

  // Shared ref for coordinating fetch requests between directions. Each
  // direction stores its in-flight promise here and waits for the other
  // direction's promise before starting its own fetch.
  const fetchPromisesRef = useRef<FetchPromises>({previous: null, next: null});

  // Tracks the current run path so that `useFetchMore` can detect stale cross-
  // run responses that resolved after a run switch. The render-time assignment
  // keeps it current; the effect cleanup clears it on unmount so that in-flight
  // `fetchMore` responses from a destroyed component instance are discarded by
  // the `updateQuery` guard.
  const runPath = `${entityName}/${projectName}/${runName}`;
  const currentRunPathRef = useRef(runPath);
  // oxlint-disable-next-line react/refs
  currentRunPathRef.current = runPath;
  useEffect(
    () => () => {
      currentRunPathRef.current = '';
    },
    []
  );

  const isGloballyBlocked = skip || loading || isRetrying;
  const isPreviousBusy =
    isGloballyBlocked || isLoadingPrevious || isRetryingPrevious;
  const isNextBusy = isGloballyBlocked || isLoadingNext || isRetryingNext;

  const isRequestInProgress = isPreviousBusy || isNextBusy;

  const [retryInitialLoad, retryError] = useRetryInitialLoad({
    isBlocked: isRequestInProgress,
    refetch,
    setIsRetrying,
  });

  const emptyLogLines = useEmptyLogLines();
  const state = data?.project?.run?.state;
  const logLines = data?.project?.run?.logLines ?? emptyLogLines;
  const startCursor = logLines.pageInfo?.startCursor;
  const endCursor = logLines.pageInfo?.endCursor;
  const hasPreviousPage = useImprovedPagination
    ? (logLines.pageInfo?.hasPreviousPage ?? false)
    : false;
  const hasNextPage = useImprovedPagination
    ? (logLines.pageInfo?.hasNextPage ?? false)
    : false;

  const [fetchMorePrevious, fetchMorePreviousError] = useFetchMore({
    currentRunPathRef,
    cursor: startCursor,
    direction: 'previous',
    entityName,
    fetchMore,
    fetchPromisesRef,
    isBlocked: isPreviousBusy || !hasPreviousPage,
    pageSize,
    projectName,
    runName,
    setIsLoading: setIsLoadingPrevious,
    setIsRetrying: setIsRetryingPrevious,
  });

  const canPoll =
    useImprovedPagination && state === 'running' && pollInterval > 0;

  // For active runs, allow fetching even when 'hasNextPage' is false - new logs
  // may have arrived since the last query. Only block on 'hasNextPage' when the
  // run has terminated and there's truly nothing more to fetch.
  const isNextBlocked = isNextBusy || (!hasNextPage && !canPoll);

  const [fetchMoreNext, fetchMoreNextError] = useFetchMore({
    currentRunPathRef,
    cursor: endCursor,
    direction: 'next',
    entityName,
    fetchMore,
    fetchPromisesRef,
    isBlocked: isNextBlocked,
    pageSize,
    projectName,
    runName,
    setIsLoading: setIsLoadingNext,
    setIsRetrying: setIsRetryingNext,
  });

  // If the initial query returns zero logs, 'endCursor' is null and
  // 'fetchMoreNext' becomes a no-op. Fall back to 'refetch' so polling can
  // still discover newly ingested logs.
  const pollFetchNext = useCallback(() => {
    if (endCursor) {
      fetchMoreNext();
    } else if (!isNextBlocked) {
      refetch();
    }
  }, [endCursor, fetchMoreNext, isNextBlocked, refetch]);

  const {isPolling, togglePolling} = usePolling({
    canPoll,
    fetchNext: pollFetchNext,
    hasNextPage,
    pollInterval,
  });

  const result = {
    error: retryError || error,
    loading: isRetrying || loading,
    logLines,
    retryInitialLoad,
  };

  return !useImprovedPagination
    ? result
    : {
        ...result,
        canPoll,
        fetchMoreNext,
        fetchMoreNextError,
        fetchMorePrevious,
        fetchMorePreviousError,
        hasNext: hasNextPage || canPoll,
        hasPrevious: hasPreviousPage,
        isLoadingNext,
        isLoadingPrevious,
        isPolling,
        isRetryingNext,
        isRetryingPrevious,
        togglePolling,
      };
}

type FetchDirection = 'previous' | 'next';

type FetchPromises = {
  previous: Promise<void> | null;
  next: Promise<void> | null;
};

type UseFetchMoreArgs = {
  currentRunPathRef: React.MutableRefObject<string>;
  cursor?: string | null;
  direction: FetchDirection;
  entityName: string;
  fetchMore: QueryResult<
    RunLogLinesQuery,
    RunLogLinesQueryVariables
  >['fetchMore'];
  fetchPromisesRef: React.MutableRefObject<FetchPromises>;
  isBlocked: boolean;
  pageSize: number;
  projectName: string;
  runName: string;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsRetrying: Dispatch<SetStateAction<boolean>>;
};

function useFetchMore({
  currentRunPathRef,
  cursor,
  direction,
  entityName,
  fetchMore,
  fetchPromisesRef,
  isBlocked,
  pageSize,
  projectName,
  runName,
  setIsLoading,
  setIsRetrying,
}: UseFetchMoreArgs): [() => void, ApolloError | undefined] {
  const [fetchError, setFetchError] = useState<ApolloError | undefined>();

  const isPrevious = direction === 'previous';
  const otherDirection = isPrevious ? 'next' : 'previous';

  const doFetchMore = useCallback(() => {
    // Without a cursor (i.e. the initial fetch returned no logs), 'fetchMore'
    // would ask for the first page (from the beginning, assuming a poll
    // request). Since logs are displayed tail-first, callers should fall back
    // to 'refetch' instead, which retries the initial fetch and returns the
    // most recent page of logs.
    if (isBlocked || !cursor) {
      return;
    }

    const setLoadingState = fetchError != null ? setIsRetrying : setIsLoading;
    setLoadingState(true);

    // Explicitly set unused pagination variables to undefined to override the
    // original query variables (Apollo merges 'fetchMore' variables).
    const variables: {
      first?: number;
      after?: string;
      last?: number;
      before?: string;
    } = isPrevious
      ? {last: pageSize, before: cursor, first: undefined, after: undefined}
      : {first: pageSize, after: cursor, last: undefined, before: undefined};

    const doFetch = async () => {
      if (fetchPromisesRef.current[direction]) {
        console.warn(
          `Unexpectedly found request in progress for '${direction}' run logs.`
        );
        return;
      }

      // Wait for the other direction's fetch to complete if in progress. This
      // prevents race conditions in Apollo's cache updates while ensuring
      // requests don't get dropped.
      const otherPromise = fetchPromisesRef.current[otherDirection];
      if (otherPromise) {
        await otherPromise;
      }

      await fetchMore({
        variables: {
          entityName,
          projectName,
          runName,
          useImprovedPagination: true,
          ...variables,
        },
        updateQuery: (resultPrev, {fetchMoreResult: result}) => {
          // Discard stale 'fetchMore' results that finished after a run switch.
          if (
            `${entityName}/${projectName}/${runName}` !==
            currentRunPathRef.current
          ) {
            return resultPrev;
          }

          const data = result?.project?.run?.logLines;
          const dataPrev = resultPrev?.project?.run?.logLines;
          if (!data?.edges.length) {
            return resultPrev;
          }

          const edges = isPrevious
            ? [...data.edges, ...(dataPrev?.edges ?? [])]
            : [...(dataPrev?.edges ?? []), ...data.edges];

          const pageInfo = isPrevious
            ? {
                ...data.pageInfo,
                endCursor: dataPrev?.pageInfo.endCursor,
                hasNextPage: dataPrev?.pageInfo.hasNextPage,
              }
            : {
                ...data.pageInfo,
                startCursor: dataPrev?.pageInfo.startCursor,
                hasPreviousPage: dataPrev?.pageInfo.hasPreviousPage,
              };

          return {
            ...result,
            project: {
              ...result!.project,
              run: {
                ...result!.project!.run,
                logLines: {
                  ...data,
                  edges,
                  pageInfo,
                },
              },
            },
          } as RunLogLinesQuery;
        },
      });
    };

    const fetchPromise = doFetch()
      .then(() => setFetchError(undefined))
      .catch(setFetchError)
      .finally(() => {
        fetchPromisesRef.current[direction] = null;
        setLoadingState(false);
      });

    fetchPromisesRef.current[direction] = fetchPromise;
  }, [
    currentRunPathRef,
    cursor,
    direction,
    entityName,
    fetchError,
    fetchMore,
    fetchPromisesRef,
    isBlocked,
    isPrevious,
    otherDirection,
    pageSize,
    projectName,
    runName,
    setIsLoading,
    setIsRetrying,
  ]);

  return [doFetchMore, fetchError];
}

function useEmptyLogLines(): LogLines {
  return useMemo(
    () => ({
      edges: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    }),
    []
  );
}
